import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Globe, Sparkles, Leaf, Target, Bot, BarChart3, Zap, Shield } from 'lucide-react';
export function PricingSlide() {
  const [customerValue, setCustomerValue] = useState(2500);
  const [targetCustomers, setTargetCustomers] = useState(8);
  const [selectedPlan, setSelectedPlan] = useState<'grow' | 'boost' | 'delegate'>('grow');
  // Conservative, research-backed ROI models:
  //
  // GROW (SEO-only):
  // - SEO takes 6-12 months to show meaningful results (Ahrefs, Moz data)
  // - Average local SEO generates 2-5 new customers/mo after month 6-8
  // - Break-even: month 8 (slow build, then compounds)
  // - Ramp: near-zero months 1-4, trickle months 5-7, full flow month 8+
  //
  // BOOST (SEO + Ads):
  // - Google Ads for local services: avg CPA $30-$80, ROAS 3-5x (WordStream)
  // - Ads produce leads week 1, but need 4-6 weeks to optimize
  // - SEO builds in background for long-term
  // - Break-even: month 2 (ads cover costs quickly)
  //
  // DELEGATE (SEO + Ads + AI Calling):
  // - AI calling reactivates old leads immediately (day 1 revenue)
  // - Handles after-hours calls (35% of calls come after 5pm - CallRail)
  // - Replaces $3-5K/mo SDR costs
  // - Break-even: month 1 (old lead reactivation + labor savings)
  const planData = {
    grow: {
      price: 447,
      annual: 5364,
      setup: 1000,
      name: 'Grow',
      // Very slow ramp — SEO reality. Near zero months 1-4, trickle 5-7, full 8+
      monthlyLeadRamp: [0.0, 0.0, 0.05, 0.1, 0.15, 0.25, 0.4, 0.65, 0.8, 0.9, 1.0, 1.0],
      note: 'SEO is a slow burn — months 1-4 build your foundation. Month 8+ is where rankings compound and leads flow consistently.'
    },
    boost: {
      price: 747,
      annual: 8964,
      setup: 1000,
      name: 'Boost',
      // Ads produce leads fast, SEO builds behind
      monthlyLeadRamp: [0.3, 0.55, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0],
      note: 'Ads generate leads from week 1. Higher upfront ad spend, but by month 2 revenue covers all costs. SEO compounds behind the scenes.'
    },
    delegate: {
      price: 1247,
      annual: 14964,
      setup: 2000,
      name: 'Delegate',
      // Immediate impact from old lead reactivation + after-hours coverage
      monthlyLeadRamp: [0.6, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0],
      note: 'ROI almost immediately — AI reactivates old leads sitting in your CRM, handles after-hours calls, and cuts labor costs. No sick days, no vacations.'
    }
  };
  const current = planData[selectedPlan];
  const totalCost = current.annual + current.setup;
  // Year-1 revenue using conservative ramp
  const monthlyFullLeads = targetCustomers;
  const yearRevenue = current.monthlyLeadRamp.reduce((sum, ramp) => {
    return sum + monthlyFullLeads * ramp * customerValue;
  }, 0);
  const netProfit = yearRevenue - totalCost;
  // Break-even: which month does cumulative revenue exceed cumulative cost?
  let cumulativeRevenue = 0;
  let cumulativeCost = current.setup;
  let breakEvenMonth = 13; // 13 means "not within year 1"
  for (let m = 0; m < 12; m++) {
    cumulativeRevenue += monthlyFullLeads * current.monthlyLeadRamp[m] * customerValue;
    cumulativeCost += current.price;
    if (cumulativeRevenue >= cumulativeCost && breakEvenMonth === 13) {
      breakEvenMonth = m + 1;
    }
  }
  const formatCurrency = (val: number) => {
    if (Math.abs(val) >= 1000000) return (val < 0 ? '-' : '') + '$' + (Math.abs(val) / 1000000).toFixed(1) + 'M';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const pricingTiers = [{
    name: 'Grow',
    price: 447,
    desc: 'Organic-first growth for local businesses & coaches',
    features: ['AI-SEO content strategy', 'Weekly optimization calls', 'Advanced keyword targeting', 'Organic traffic growth', 'Performance dashboards'],
    excluded: ['Paid ads management', 'AI calling agents'],
    cta: 'Start with Grow',
    href: 'https://letlois.com/grow',
    highlight: true,
    badge: 'MOST POPULAR',
    focusIcon: Leaf,
    focusLabel: 'Organic Growth',
    focusColor: 'text-green-400',
    focusBg: 'bg-green-500/10'
  }, {
    name: 'Boost',
    price: 747,
    desc: 'Organic + paid ads for fast lead generation',
    features: ['Everything in Grow', 'FB/IG/Google Ads management', 'Campaign creation & testing', 'Landing page optimization', 'Dual traffic strategy'],
    excluded: ['AI calling agents'],
    cta: 'Choose Boost',
    href: 'https://letlois.com/boost',
    highlight: false,
    focusIcon: Target,
    focusLabel: 'Paid Growth',
    focusColor: 'text-red-400',
    focusBg: 'bg-red-500/10'
  }, {
    name: 'Delegate',
    price: 1247,
    desc: 'Full automation — we run it, you own it',
    features: ['Everything in Boost', 'AI calling agents (24/7)', 'AI texting automation', 'Lead qualification', 'White-label AI agents'],
    excluded: [],
    cta: 'Choose Delegate',
    href: 'https://letlois.com/delegate',
    highlight: false,
    focusIcon: Bot,
    focusLabel: 'Reduce Labor',
    focusColor: 'text-blue-400',
    focusBg: 'bg-blue-500/10'
  }];
  return <section id="pricing" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-369">
      <div className="max-w-7xl mx-auto w-full" data-id="element-370">
        <div className="text-center mb-16" data-id="element-371">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-372">
            Three Plans. Pick Your Path.
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-373">
            Choose the growth strategy that fits your business.
          </motion.p>
        </div>

        {/* Pro Plan - Big Deal Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-12 bg-gradient-to-br from-primary/10 via-dark-card to-blue-900/10 border border-primary/20 rounded-2xl p-8 relative overflow-hidden" data-id="element-374">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[80px] pointer-events-none" data-id="element-375" />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-376">
            <div className="flex-shrink-0" data-id="element-377">
              <div className="w-20 h-20 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center" data-id="element-378">
                <Sparkles size={36} className="text-primary" data-id="element-379" />
              </div>
            </div>

            <div className="flex-1 text-center md:text-left" data-id="element-380">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2" data-id="element-381">
                <h3 className="text-2xl font-bold text-white" data-id="element-382">
                  Pro Plan — Included on All Plans
                </h3>
                <span className="text-xs font-bold bg-primary/20 text-primary px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-383">
                  $97/mo value
                </span>
              </div>
              <p className="text-text-muted text-lg mb-4" data-id="element-384">
                Every plan includes your own{' '}
                <span className="text-white font-semibold" data-id="element-385">
                  marketing automation command center
                </span>{' '}
                — a full platform that manages your metrics, tracks every lead,
                and demands growth.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start" data-id="element-386">
                <span className="flex items-center text-sm text-white/80" data-id="element-387">
                  <BarChart3 size={14} className="text-primary mr-1.5" data-id="element-388" />
                  Real-time analytics
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-389">
                  <Zap size={14} className="text-primary mr-1.5" data-id="element-390" />
                  Automated workflows
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-391">
                  <Shield size={14} className="text-primary mr-1.5" data-id="element-392" />
                  Custom 3-layer website
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-393">
                  <Check size={14} className="text-primary mr-1.5" data-id="element-394" />
                  Foundations call
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pricing Cards - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 max-w-5xl mx-auto" data-id="element-395">
          {pricingTiers.map((tier, index) => <motion.div key={tier.name} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`relative flex flex-col p-6 rounded-2xl bg-dark-card border transition-all duration-300 ${tier.highlight ? 'border-primary shadow-[0_0_30px_rgba(26,139,246,0.15)] md:scale-105 z-10' : 'border-white/10 hover:border-white/20'}`} data-id="element-396">
              {tier.highlight && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-397">
                  {tier.badge}
                </div>}

              {/* Focus icon + label - top right */}
              <div className="absolute top-5 right-5" data-id="element-398">
                <div className={`flex items-center gap-1.5 ${tier.focusBg} px-2.5 py-1.5 rounded-lg`} data-id="element-399">
                  <tier.focusIcon size={14} className={tier.focusColor} data-id="element-400" />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${tier.focusColor}`} data-id="element-401">
                    {tier.focusLabel}
                  </span>
                </div>
              </div>

              <div className="mb-6 pr-4" data-id="element-402">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-403">
                  {tier.name}
                </h3>
                <p className="text-sm text-text-muted mb-4 leading-relaxed pr-16" data-id="element-404">
                  {tier.desc}
                </p>
                <div className="flex items-baseline" data-id="element-405">
                  <span className="text-4xl font-bold text-primary" data-id="element-406">
                    ${tier.price}
                  </span>
                  <span className="text-text-dim ml-2" data-id="element-407">/mo</span>
                </div>
              </div>

              <div className="flex-1 space-y-3 mb-8" data-id="element-408">
                {tier.features.map((feature, i) => <div key={i} className="flex items-start text-sm text-white/90" data-id="element-409">
                    <Check size={16} className="text-green-500 mr-2 mt-0.5 shrink-0" data-id="element-410" />
                    <span data-id="element-411">{feature}</span>
                  </div>)}
                {tier.excluded.map((feature, i) => <div key={i} className="flex items-start text-sm text-text-dim/50" data-id="element-412">
                    <X size={16} className="text-red-500/50 mr-2 mt-0.5 shrink-0" data-id="element-413" />
                    <span data-id="element-414">{feature}</span>
                  </div>)}
              </div>

              <a href={tier.href} target="_blank" rel="noopener noreferrer" className={`w-full py-3 rounded-full font-bold transition-all text-center block ${tier.highlight ? 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/25' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-id="element-415">
                {tier.cta}
              </a>
            </motion.div>)}
        </div>

        {/* ROI Calculator */}
        <div className="bg-gradient-to-br from-dark-card to-primary/5 rounded-2xl p-8 border border-white/10 mb-16" data-id="element-416">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-417">
            Calculate Your ROI
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-418">
            Conservative projections based on Ahrefs, WordStream & CallRail
            industry data
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-419">
            <div className="space-y-6" data-id="element-420">
              <div data-id="element-421">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-422">
                  Average Customer Value
                </label>
                <div className="relative" data-id="element-423">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-dim" data-id="element-424">
                    $
                  </span>
                  <input type="number" value={customerValue} onChange={e => setCustomerValue(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-425" />
                </div>
              </div>

              <div data-id="element-426">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-427">
                  Target New Customers / Month
                </label>
                <input type="number" value={targetCustomers} onChange={e => setTargetCustomers(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 px-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-428" />
              </div>

              <div data-id="element-429">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-430">
                  Select Plan
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-431">
                  {(['grow', 'boost', 'delegate'] as const).map(plan => <button key={plan} onClick={() => setSelectedPlan(plan)} className={`py-2 rounded-lg text-sm font-medium transition-all ${selectedPlan === plan ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-white/5 text-text-muted hover:bg-white/10'}`} data-id="element-432">
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </button>)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4" data-id="element-433">
              <div className="bg-black/20 rounded-xl p-6 border border-white/5" data-id="element-434">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-435">
                  <div data-id="element-436">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-437">
                      Year 1 Revenue
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-438">
                      {formatCurrency(yearRevenue)}
                    </p>
                  </div>
                  <div data-id="element-439">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-440">
                      Total Investment
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-441">
                      {formatCurrency(totalCost)}
                    </p>
                  </div>
                  <div data-id="element-442">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-443">
                      Net Profit
                    </p>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-444">
                      {formatCurrency(netProfit)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Break-even highlight */}
              <motion.div key={selectedPlan + customerValue + targetCustomers} initial={{
              scale: 0.98,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`rounded-xl p-5 border ${breakEvenMonth <= 12 ? 'bg-green-500/10 border-green-500/20' : 'bg-yellow-500/10 border-yellow-500/20'}`} data-id="element-445">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-446">
                  <div data-id="element-447">
                    <p className={`font-bold text-lg ${breakEvenMonth <= 12 ? 'text-green-400' : 'text-yellow-400'}`} data-id="element-448">
                      {breakEvenMonth <= 12 ? `Break-even: Month ${breakEvenMonth}` : 'Break-even: 12+ months'}
                      {breakEvenMonth <= 12 && selectedPlan === 'grow' && ' — then profit skyrockets'}
                    </p>
                    <p className="text-text-muted text-sm mt-1" data-id="element-449">
                      {current.note}
                    </p>
                  </div>

                  {/* Mini month timeline */}
                  <div className="flex items-end gap-1" data-id="element-450">
                    {current.monthlyLeadRamp.map((ramp, i) => <div key={i} className="flex flex-col items-center" data-id="element-451">
                        <div className={`w-3 rounded-t-sm ${i < breakEvenMonth ? 'bg-red-500/40' : 'bg-green-500/60'}`} style={{
                      height: `${Math.max(ramp * 32, 2)}px`
                    }} data-id="element-452" />
                        {(i === 0 || i === 5 || i === 11) && <span className="text-[7px] text-text-dim mt-0.5" data-id="element-453">
                            {i + 1}
                          </span>}
                      </div>)}
                    <span className="text-[7px] text-text-dim ml-0.5 self-end mb-0.5" data-id="element-454">
                      mo
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Plan-specific insight */}
              <div className="bg-blue-900/10 border border-blue-500/15 rounded-lg px-4 py-3" data-id="element-455">
                <p className="text-xs text-blue-300" data-id="element-456">
                  <span className="font-bold" data-id="element-457">
                    {current.name} Plan Insight:
                  </span>{' '}
                  {selectedPlan === 'grow' && "SEO is a compounding asset. Months 1-4 you're building domain authority — expect minimal leads. Months 5-7 rankings start climbing. Month 8+ is where it pays off big. Average local SEO ROI after 12 months: 200-300% (Ahrefs)."}
                  {selectedPlan === 'boost' && 'Google Ads for local services average $30-80 CPA with 3-5x ROAS (WordStream). Ads produce leads from week 1 while SEO builds behind the scenes. Month 2 typically covers all costs including ad spend.'}
                  {selectedPlan === 'delegate' && 'AI agents reactivate old leads from day 1 — most businesses have 100+ untouched leads in their CRM. Handles after-hours calls (35% of calls come after 5pm — CallRail). No sick days, no vacations, no lunch breaks. Similar booking rates to human SDRs at a fraction of the cost.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Caveat & Trust */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-458">
          <div className="bg-blue-900/10 border border-blue-500/20 rounded-xl p-4 mb-8" data-id="element-459">
            <p className="text-sm text-blue-200" data-id="element-460">
              <span className="font-bold" data-id="element-461">Honest Note:</span> We're built on
              GoHighLevel—the most powerful (and least supported) platform in
              the world. That's why we exist. We're the support team you need.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-text-dim" data-id="element-462">
            <span className="flex items-center" data-id="element-463">
              <Check size={16} className="text-primary mr-2" data-id="element-464" /> Join 200+
              businesses
            </span>
            <span className="flex items-center" data-id="element-465">
              <Check size={16} className="text-primary mr-2" data-id="element-466" /> Setup support
              included
            </span>
            <span className="flex items-center" data-id="element-467">
              <Check size={16} className="text-primary mr-2" data-id="element-468" /> Money-back
              guarantee
            </span>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center" data-id="element-469">
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => {
          document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="bg-primary hover:bg-primary-hover text-white text-xl font-bold py-5 px-10 rounded-full shadow-xl shadow-primary/30 mb-4 flex items-center mx-auto" data-id="element-470">
            <Globe className="mr-3" data-id="element-471" />
            Schedule an Onboarding Call
          </motion.button>
          <p className="text-text-muted" data-id="element-472">Pick a plan above to get started.</p>
        </div>
      </div>
    </section>;
}